import React, { useEffect, useState } from "react";
import { ArrowLeft, Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { SITE_CONFIG, buildWhatsAppUrl } from "../config/site";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Missing details",
        description: "Please fill name, email and your message.",
      });
      return;
    }
    const store = JSON.parse(localStorage.getItem("blanqo_contacts") || "[]");
    store.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("blanqo_contacts", JSON.stringify(store));
    toast({
      title: "Message Sent",
      description: `Thank you ${form.name}. Our team will get back to you shortly.`,
    });
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const waUrl = buildWhatsAppUrl(
    SITE_CONFIG.whatsapp.phone,
    SITE_CONFIG.whatsapp.defaultMessage
  );

  return (
    <div className="bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#0b1c2c] pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none flex items-center justify-center overflow-hidden">
          <p
            className="text-[140px] md:text-[220px] lg:text-[280px] font-light tracking-[0.15em] text-white/[0.03] whitespace-nowrap"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            BLANQO
          </p>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#c9a96e] mb-8 text-xs tracking-[0.25em] uppercase transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#c9a96e]" />
            <span className="text-[#c9a96e] text-xs tracking-[0.4em] uppercase">
              Get In Touch
            </span>
          </div>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.05]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Contact <span className="italic text-[#c9a96e]">Us</span>
          </h1>
          <p className="text-white/60 text-lg mt-6 max-w-2xl leading-relaxed">
            We'd love to hear from you — whether it's a booking, a question
            about a delicate garment, or simply to say hello.
          </p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 relative z-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <a
            href="tel:+917075551876"
            className="bg-white p-6 shadow-[0_15px_40px_-15px_rgba(11,28,44,0.25)] border border-transparent hover:border-[#c9a96e]/40 transition-colors group"
          >
            <Phone className="w-6 h-6 text-[#8a6f3e] mb-4" strokeWidth={1.4} />
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f3e] mb-2">
              Call Us
            </p>
            <p
              className="text-xl font-light text-[#0b1c2c] group-hover:text-[#8a6f3e] transition-colors"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              +91 70755 51876
            </p>
          </a>
          <a
            href="mailto:hello@blanqo.in"
            className="bg-white p-6 shadow-[0_15px_40px_-15px_rgba(11,28,44,0.25)] border border-transparent hover:border-[#c9a96e]/40 transition-colors group"
          >
            <Mail className="w-6 h-6 text-[#8a6f3e] mb-4" strokeWidth={1.4} />
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f3e] mb-2">
              Email
            </p>
            <p
              className="text-xl font-light text-[#0b1c2c] group-hover:text-[#8a6f3e] transition-colors break-all"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              hello@blanqo.in
            </p>
          </a>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white p-6 shadow-[0_15px_40px_-15px_rgba(11,28,44,0.25)] border border-transparent hover:border-[#c9a96e]/40 transition-colors group"
          >
            <MessageCircle className="w-6 h-6 text-[#8a6f3e] mb-4" strokeWidth={1.4} />
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f3e] mb-2">
              WhatsApp
            </p>
            <p
              className="text-xl font-light text-[#0b1c2c] group-hover:text-[#8a6f3e] transition-colors"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Chat with us
            </p>
          </a>
          <div className="bg-white p-6 shadow-[0_15px_40px_-15px_rgba(11,28,44,0.25)]">
            <Clock className="w-6 h-6 text-[#8a6f3e] mb-4" strokeWidth={1.4} />
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f3e] mb-2">
              Business Hours
            </p>
            <p
              className="text-xl font-light text-[#0b1c2c] leading-snug"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Mon – Sat
              <br />
              10:00 AM – 7:00 PM
            </p>
          </div>
        </div>
      </section>

      {/* Main: Form + Addresses */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#c9a96e]" />
              <span className="text-[#c9a96e] text-xs tracking-[0.4em] uppercase">
                Send a Message
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-light text-[#0b1c2c] mb-4 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              We'll be in <span className="italic text-[#8a6f3e]">touch</span>.
            </h2>
            <p className="text-[#4a5568] mb-10 max-w-xl">
              Tell us a little about how we can help and our team will reach
              out within one business day.
            </p>

            <form onSubmit={submit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#4a5568] mb-2 block">
                    Full Name
                  </Label>
                  <Input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    className="rounded-none border-0 border-b border-[#0b1c2c]/20 focus-visible:ring-0 focus-visible:border-[#c9a96e] px-0 h-11 bg-transparent"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#4a5568] mb-2 block">
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    className="rounded-none border-0 border-b border-[#0b1c2c]/20 focus-visible:ring-0 focus-visible:border-[#c9a96e] px-0 h-11 bg-transparent"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#4a5568] mb-2 block">
                    Phone
                  </Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91"
                    className="rounded-none border-0 border-b border-[#0b1c2c]/20 focus-visible:ring-0 focus-visible:border-[#c9a96e] px-0 h-11 bg-transparent"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#4a5568] mb-2 block">
                    Subject
                  </Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => update("subject", e.target.value)}
                    placeholder="How can we help?"
                    className="rounded-none border-0 border-b border-[#0b1c2c]/20 focus-visible:ring-0 focus-visible:border-[#c9a96e] px-0 h-11 bg-transparent"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#4a5568] mb-2 block">
                  Message
                </Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us a bit about your garments, occasion, or query..."
                  rows={5}
                  className="rounded-none border border-[#0b1c2c]/15 focus-visible:ring-0 focus-visible:border-[#c9a96e] bg-transparent resize-none"
                />
              </div>

              <Button
                type="submit"
                className="bg-[#0b1c2c] hover:bg-[#142a40] text-white rounded-none h-12 px-10 tracking-[0.25em] text-xs uppercase inline-flex items-center gap-3"
              >
                Send Message
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>

          {/* Addresses */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#f5f1ea] border border-[#c9a96e]/25 p-7">
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="w-5 h-5 text-[#8a6f3e] mt-0.5" strokeWidth={1.5} />
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f3e]">
                  Outlet
                </p>
              </div>
              <p
                className="text-2xl font-light text-[#0b1c2c] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Kondapur, Hyderabad
              </p>
              <p className="text-[#4a5568] leading-relaxed text-sm mb-4">
                #205, SMR Vinay Iconia Plaza, Botanical Garden Road, Kondapur,
                Sri Maruthi Nagar Colony, Gachibowli, Serilingampalle (M),
                Hyderabad, Telangana – 500019
              </p>
              <a
                href="https://maps.app.goo.gl/bFozQAKpf627TXjGA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#8a6f3e] hover:text-[#0b1c2c] text-xs tracking-[0.25em] uppercase border-b border-[#8a6f3e]/40 pb-1 transition-colors"
              >
                View on Google Maps
                <ArrowLeft className="w-3.5 h-3.5 rotate-[135deg]" />
              </a>
            </div>

            <div className="bg-[#f5f1ea] border border-[#c9a96e]/25 p-7">
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="w-5 h-5 text-[#8a6f3e] mt-0.5" strokeWidth={1.5} />
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f3e]">
                  Registered Office
                </p>
              </div>
              <p
                className="text-2xl font-light text-[#0b1c2c] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Nizampet, Hyderabad
              </p>
              <p className="text-[#4a5568] leading-relaxed text-sm">
                B Block, Flat 410, Maruthi Elite Gated Community Apartments,
                Nizampet, Hyderabad – 500090
              </p>
              <p className="text-[#4a5568]/70 text-xs mt-4 tracking-wider">
                RASA DYAD ENTERPRISES LLP
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-[#f5f1ea] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#c9a96e]" />
              <span className="text-[#c9a96e] text-xs tracking-[0.4em] uppercase">
                Visit Our Store
              </span>
              <span className="h-px w-10 bg-[#c9a96e]" />
            </div>
            <h2
              className="text-3xl md:text-4xl font-light text-[#0b1c2c] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Find us in <span className="italic text-[#8a6f3e]">Kondapur</span>
            </h2>
          </div>
          <div className="aspect-[16/8] w-full overflow-hidden border border-[#c9a96e]/25 shadow-[0_25px_60px_-30px_rgba(11,28,44,0.4)]">
            <iframe
              title="Blanqo Kondapur Store Location"
              src="https://www.google.com/maps?q=SMR+Vinay+Iconia+Plaza,+Botanical+Garden+Rd,+Kondapur,+Hyderabad&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;